import React, { useState, useMemo } from 'react'; // Import useMemo
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { 
  Box, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Link
} from '@mui/material';
import { UserRole } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';
import { SubscriptionPlans } from './SubscriptionPlans';
// Import investmentAPI
import { investmentAPI } from '../../api/investmentAPI'; 
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { alpha } from '@mui/material/styles';

interface Props {
  onClose: () => void;
  onPlanChange?: (newRole: string) => void;
  isEmbedded?: boolean;
}

export const SubscriptionManager: React.FC<Props> = ({
  onClose,
  onPlanChange,
  isEmbedded = false
}) => {
  // Remove token from useAuth as apiRequest handles it
  const { user, logout, updateUser } = useAuth(); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  } | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const navigate = useNavigate(); // Instantiate useNavigate
  
  // Instantiate the API client
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  const currentRole = user?.role || 'unregistered';

  const handleAction = async (role: UserRole) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use user.loginId directly
      if (!user?.loginId) { 
        throw new Error('User information not found');
      }

      // Use api.changeRole instead of fetch
      const data = await api.changeRole(role, user.loginId); 

      // Remove check for response.ok, apiRequest handles errors

      // const data = await response.json(); // apiRequest returns parsed data
      
      // Handle checkout URL for upgrades requiring payment
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        // navigate(data.checkout_url); // Redirect to checkout URL
        return; // Exit early
        // const popup = window.open(data.checkout_url, '_blank', 'width=600,height=800');
        // if (!popup) {
        //   throw new Error('Popup blocked. Please allow popups for this site.');
        // }
        
        // // Wait for payment completion via message event
        // await new Promise<void>((resolve, reject) => {
        //   const handleMessage = (event: MessageEvent) => {
        //     if (event.data.type === 'PAYMENT_SUCCESS') {
        //       window.removeEventListener('message', handleMessage);
        //       popup.close();
        //       resolve();
        //     } else if (event.data.type === 'PAYMENT_ERROR' || event.data.type === 'PAYMENT_CANCELLED') {
        //       window.removeEventListener('message', handleMessage);
        //       popup.close();
        //       reject(new Error(event.data.error || 'Checkout failed'));
        //     }
        //   };
        //   window.addEventListener('message', handleMessage);
          
        //   // Set a timeout to handle cases where no message is received
        //   setTimeout(() => {
        //     window.removeEventListener('message', handleMessage);
        //     if (popup && !popup.closed) {
        //       popup.close();
        //     }
        //     reject(new Error('Payment confirmation timed out. Please check your account status.'));
        //   }, 300000); // 5 minute timeout
        // });
      }
      
      // Update the user role
      updateUser({ role });
      
      // Call the onPlanChange callback with the new role
      onPlanChange?.(role);
      // Navigate after plan change if needed (parent might already handle it via onPlanChange)
      // navigate('/dashboard'); // Or keep current page: navigate(0) or let parent handle

      setConfirmDialog(null);
      setSuccess(true);
      
      // Close the subscription manager after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      // apiRequest might throw errors directly
      setError(err.message || 'Failed to change subscription'); 
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use user.loginId directly
      if (!user?.loginId) { 
        throw new Error('No user found');
      }

      // Use api.cancelSubscription instead of fetch
      await api.cancelSubscription(user.loginId); 

      // Remove check for response.ok, apiRequest handles errors

      setShowCancelConfirm(false);
      onClose(); // Close the modal first
      logout(); // Auth context likely handles navigation after logout
      // Explicit navigation usually not needed here as logout() should redirect
      // navigate('/'); 
    } catch (err: any) {
      // apiRequest might throw errors directly
      setError(err.message || 'Failed to cancel subscription'); 
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan: string) => {
    // Don't do anything if it's the current plan
    if (plan === currentRole) return;
    
    const isUpgrade = ['free', 'base', 'premium', 'premium_plus'].indexOf(plan) >
                      ['free', 'base', 'premium', 'premium_plus'].indexOf(currentRole);

    setSelectedPlan(plan);
    setConfirmDialog({
      open: true,
      title: isUpgrade ? 'Upgrade Plan' : 'Change Plan',
      message: isUpgrade 
        ? 'Are you sure you want to upgrade? This may require additional payment.'
        : 'Are you sure you want to change your plan? This will take effect immediately.',
      action: () => handleAction(plan as UserRole)
    });
  };

  return (
    <Box sx={{ p: isEmbedded ? 0 : 3, maxWidth: 1200, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your subscription has been updated successfully!
        </Alert>
      )}

      <Box sx={{ position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SubscriptionPlans
            onSelect={handlePlanSelect}
            currentRole={currentRole}
          />
        )}
      </Box>

      {/* Enterprise Section */}
      <Box 
        sx={{ 
          mt: 8, 
          p: 4, 
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ color: 'text.primary' }}>
          Looking for Enterprise Solutions?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          We offer customized enterprise-level solutions with advanced features, dedicated support, and flexible deployment options.
          Contact us to learn more about how we can help your organization.
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Email us at: <Link href="mailto:support@seattlemarketanalytics.com" sx={{ color: 'primary.main' }}>support@seattlemarketanalytics.com</Link>
        </Typography>
      </Box>

      {currentRole !== 'free' && currentRole !== 'unregistered' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setShowCancelConfirm(true)}
            disabled={loading}
            sx={{ 
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.dark',
                color: 'common.white'
              }
            }}
          >
            Cancel Subscription
          </Button>
        </Box>
      )}

      {confirmDialog && (
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(null)}>
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button 
              onClick={() => confirmDialog.action()}
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your subscription? You will lose access to premium features 
            at the end of your current billing period.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)}>Keep Subscription</Button>
          <Button 
            onClick={handleCancelSubscription}
            color="error"
            disabled={loading}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};